
import { useState, useEffect } from "react";
import { useDropzone } from 'react-dropzone';
import { Button } from "@/components/ui/button";
import { ExternalLink } from "lucide-react";

type ReadingPhotoUploadProps = {
  previewUrl: string | null;
  isUploading: boolean;
  onFileSelect: (file: File) => void;
};

export const ReadingPhotoUpload = ({ previewUrl, isUploading, onFileSelect }: ReadingPhotoUploadProps) => {
  const [imageError, setImageError] = useState(false);
  
  const onDrop = (acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      onFileSelect(file);
      setImageError(false);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': []
    },
    maxFiles: 1,
    multiple: false
  });

  const handleImageError = () => {
    setImageError(true);
    console.error('Error loading image:', previewUrl);
  };

  return (
    <div
      {...getRootProps()}
      className={`border-2 border-dashed rounded-lg p-4 cursor-pointer transition-colors
        ${isDragActive ? 'border-primary bg-primary/5' : 'border-muted-foreground/25'}
        ${isUploading ? 'opacity-50' : ''}`}
    >
      <input {...getInputProps()} />
      <div className="text-center space-y-3">
        {previewUrl && !imageError ? (
          <div className="space-y-2">
            <img
              src={previewUrl}
              alt="Preview"
              className="max-h-32 mx-auto object-contain"
              onError={handleImageError}
            />
            <p className="text-sm text-muted-foreground">
              {isUploading ? "Subiendo..." : "Imagen guardada"}
            </p>
            {/* Add View Image button */}
            <Button
              variant="outline"
              size="sm"
              asChild
              onClick={(e) => e.stopPropagation()}
            >
              <a 
                href={previewUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2"
                onClick={(e) => e.stopPropagation()}
              >
                <ExternalLink className="w-4 h-4" />
                Ver imagen
              </a>
            </Button>
          </div>
        ) : (
          <>
            <div className="text-muted-foreground">
              {isDragActive ? (
                <p>Suelta la imagen aquí ...</p>
              ) : (
                <p>Arrastra una imagen o haz clic para seleccionar</p>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              Solo archivos de imagen (PNG, JPG, GIF)
            </p>
          </>
        )}
        {isUploading && <p className="text-sm text-muted-foreground">Subiendo...</p>}
      </div>
    </div>
  );
};
